import React from "react";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import { FileText } from "lucide-react";

const SECTIONS = [
  { title:"1. Acceptance of Terms", items:[
    ["Agreement","By creating a PilotLink USA account you agree to these Terms and our Privacy Policy."],
    ["Eligibility","You must be 18+ and legally permitted to operate as a pilot car operator in your jurisdiction."],
    ["Updates","Material changes notified 30 days in advance. Continued use constitutes acceptance."],
  ]},
  { title:"2. Platform Services", items:[
    ["Driver Services","Profile, load board, certification verification (Verified Badge), training courses, and invoicing."],
    ["Company Services","Post loads, search and hire verified drivers, manage loads, verify credentials."],
    ["Training & Courses","The PPCA Crash Course is a paid preparation guide — not an official PPCA or NPCA product. No guarantee of passing any official examination."],
    ["Third-Party Services","Stripe (payments) and mapping services are governed by their own terms."],
  ]},
  { title:"3. User Responsibilities", items:[
    ["Accurate Information","False credential claims (fake certifications, false insurance) result in immediate termination and may be reported to authorities."],
    ["Professional Conduct","Comply with all applicable federal, state, and local laws and follow all permit requirements."],
    ["Prohibited Activities","No scraping, bots, reverse engineering, fraudulent profiles, harassment, or illegal use."],
    ["Account Security","You are responsible for your account credentials. Report unauthorized access immediately."],
  ]},
  { title:"4. Payments & Refunds", items:[
    ["Course Purchases","Charged at checkout via Stripe. See our Refund Policy for eligibility and timelines."],
    ["Membership Plans","Billed monthly or annually. Cancellation stops future billing but does not refund the current period."],
    ["Stripe Terms","Subject to the Stripe Services Agreement at stripe.com/legal."],
    ["Taxes","You are responsible for applicable taxes on purchases."],
  ]},
  { title:"5. Intellectual Property", items:[
    ["Platform Content","All course materials, infographics, exam questions, branding, and code are property of PilotLink USA."],
    ["Prohibited Copying","You may not reproduce, resell, or distribute any PilotLink course content without written permission."],
    ["DMCA","Report copyright infringement: dmca@pilotlinkusa.com"],
  ]},
  { title:"6. Verified Badge", items:[
    ["Badge Scope","The Verified Badge indicates completion of our internal exam prep. It does not replace official PPCA/NPCA certification."],
    ["Accuracy","You are solely responsible for keeping official certifications current."],
    ["Revocation","Badges may be revoked for fraudulent credential uploads or misrepresentation."],
  ]},
  { title:"7. Liability & Disputes", items:[
    ["No Guarantee","PilotLink USA does not guarantee job placement, income, or exam passage."],
    ["Liability Cap","Total liability shall not exceed the amount you paid us in the 12 months preceding any claim."],
    ["Governing Law","Governed by the laws of the State of Texas. Disputes resolved through binding AAA arbitration."],
    ["Contact First","Contact support@pilotlinkusa.com before initiating any dispute."],
  ]},
  {
  title: "8. Certification Policy (PPCA & State Exam)",
  items: [
    [
      "PPCA Certification",
      "Upon passing the PPCA exam, your results are automatically transmitted to the Platform. A Verified PPCA Badge will be issued and attached to your profile, including a unique QR code for employer verification. Once issued, your PPCA badge obligations are fully satisfied and no further quiz retakes are required. Users who fail the PPCA exam may retake the exam until a passing result is achieved. Course access may be reset at no additional charge for users who have completed 100% of required course modules."
    ],
    [
      "State Exam Certification",
      "Users may retake their State Exam until a passing result is achieved. After passing, users must upload their certification documentation and expiration date to activate their Learning Cycle. The Learning Cycle grants continued access to Crash Course materials until the certification expires. Because the user has already passed the exam, no further quiz retakes are required during the active cycle. State regulations may change periodically, and Crash Course content will be updated to reflect current requirements."
    ],
    [
      "Pass Guarantee",
      "The Pass Guarantee applies only to enrolled users who have completed 100% of course content prior to taking their PPCA or State Exam. The Platform does not provide monetary refunds under any circumstances."
    ],
    [
      "Fraud & Misrepresentation",
      "Users are responsible for ensuring the accuracy of all uploaded documentation. Fraudulent, falsified, or misleading certification submissions may result in suspension or termination of Platform access and revocation of badges or learning cycles."
    ]
  ]
},
];

export default function TermsOfService({ isDarkMode, toggleTheme }) {
  const D      = isDarkMode;
  const bg     = D ? "#0d1f3c" : "#f4f7fb";
  const panel  = D ? "#142a4d" : "#ffffff";
  const mid    = D ? "#1a3358" : "#f0f4f8";
  const border = D ? "rgba(255,255,255,0.10)" : "#e2e8f0";
  const text   = D ? "#f0f4f8" : "#0d1f3c";
  const textMid= D ? "rgba(240,244,248,0.80)" : "#334155";
  const textSub= D ? "rgba(240,244,248,0.55)" : "#64748b";
  const gold   = "#c9971c";

  return (
    <div style={{ minHeight:"100vh", background:bg, fontFamily:"'Inter',sans-serif", transition:"background .3s" }}>
      <Navbar isDarkMode={D} toggleTheme={toggleTheme}/>
      <div style={{ background:"linear-gradient(135deg,#0d1f3c,#1a3358)", padding:"clamp(40px,6vw,68px) 16px", textAlign:"center", borderBottom:`3px solid ${gold}` }}>
        <div style={{ width:52, height:52, borderRadius:13, background:gold, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px" }}>
          <FileText size={26} style={{ color:"#0d1f3c" }}/>
        </div>
        <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(24px,4vw,38px)", fontWeight:900, color:"#fff", margin:"0 0 10px" }}>Terms of Service</h1>
        <p style={{ fontSize:13, color:"rgba(255,255,255,0.50)", margin:0 }}>Effective July 1, 2026 · Last Updated July 2, 2026</p>
      </div>
      <div style={{ maxWidth:820, margin:"0 auto", padding:"clamp(24px,4vw,44px) clamp(14px,3vw,24px)" }}>
        <div style={{ background:"rgba(201,151,28,0.12)", border:"1.5px solid rgba(201,151,28,0.30)", borderRadius:12, padding:"14px 18px", marginBottom:20 }}>
          <p style={{ fontSize:14, color:D?"#fbbf24":"#92400e", fontWeight:600, margin:0 }}>⚠ Please read these Terms carefully before using PilotLink USA. They affect your legal rights.</p>
        </div>
        {SECTIONS.map((sec, i) => (
          <div key={i} style={{ background:panel, borderRadius:14, padding:"clamp(18px,2.5vw,24px)", marginBottom:12, border:`1px solid ${border}` }}>
            <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(15px,2vw,18px)", fontWeight:800, color:gold, margin:"0 0 16px", paddingBottom:10, borderBottom:`1px solid ${border}` }}>{sec.title}</h2>
            <div style={{ display:"flex", flexDirection:"column", gap:13 }}>
              {sec.items.map(([h, t], j) => (
                <div key={j}>
                  <div style={{ fontSize:13.5, fontWeight:700, color:text, marginBottom:4 }}>{h}</div>
                  <p style={{ fontSize:13.5, color:textMid, lineHeight:1.80, margin:0 }}>{t}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div style={{ background:mid, borderRadius:10, padding:"14px 18px", border:`1px solid ${border}`, textAlign:"center" }}>
          <p style={{ fontSize:13, color:textSub, margin:0 }}>Questions? <a href="mailto:support@pilotlinkusa.com" style={{ color:gold, fontWeight:600 }}>support@pilotlinkusa.com</a></p>
        </div>
      </div>
      <Footer isDarkMode={D}/>
    </div>
  );
}
